import { useState } from 'react';
import { Link2, Loader2 } from 'lucide-react';
import { Button, Modal, ModalBody, useToast } from '@sighthost/ui';
import { salesService, type QuotationPayLink } from '@/domains/sales/services/salesService';
import { CopyPayLink } from '@/shared/components/billing/CopyPayLink';

/**
 * QuotationPayLinkButton — fetch a quotation's payment link on demand, then
 * offer it for copy-paste.
 *
 * The link is minted per click rather than listed with the row, so no bearer
 * token is ever carried in a bulk list payload and every issuance leaves a
 * `payment.link_issued` audit row. Same shape as the one-shot cPanel SSO URL
 * (CLAUDE.md §4.7): sensitive value on request, never in a listing.
 *
 * QUOTATIONS ONLY (owner decision 2026-07-27). The EF is the enforcement
 * point — it returns 409 `not_a_quotation` for a converted customer, and this
 * component surfaces that message as-is rather than pre-hiding itself, because
 * a hidden button is not a control.
 *
 * Shared by SalesQuotationsPage and SalesLeadDetailPage so the two never drift.
 */
export function QuotationPayLinkButton({
  customerProfileId,
  customerName,
  size = 'sm',
  label = 'Pay link',
  fullWidth = false,
}: {
  customerProfileId: string;
  /** Fallback greeting name if the EF cannot resolve one. */
  customerName?: string | null;
  size?: 'sm' | 'default';
  label?: string;
  fullWidth?: boolean;
}) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState<QuotationPayLink | null>(null);

  async function fetchLink() {
    setLoading(true);
    try {
      const result = await salesService.getQuotationPayLink(customerProfileId);
      setLink(result);
    } catch (e) {
      // Surface the EF's own message. 'not_a_quotation' is a legitimate
      // outcome, not a fault, and its text explains where to route instead.
      toast({
        tone: 'warning',
        title: 'No payment link available',
        body: (e as Error).message,
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Button
        size={size}
        variant="ghost"
        onClick={fetchLink}
        disabled={loading}
        className={fullWidth ? 'w-full' : undefined}
        title="Get a payment link for this quotation, ready to paste into WhatsApp or email"
        aria-label="Get quotation payment link"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
        {label}
      </Button>

      {link && (
        <Modal
          open
          onOpenChange={(o) => { if (!o) setLink(null); }}
          title={`Payment link - ${link.reference}`}
          description="Paste this to the customer. The link is unique to this quotation and stops working once it has been paid."
          size="wide"
        >
          <ModalBody>
            <CopyPayLink
              payUrl={link.pay_url}
              reference={link.reference}
              docLabel="quotation"
              amountZar={link.amount}
              customerName={link.customer_name ?? customerName ?? null}
              senderName={link.sender_name}
              dueLabel={formatLongDate(link.expires_at)}
              size="default"
            />
            <p className="mt-3 text-xs text-muted-foreground">
              Sent links are recorded against this customer for audit purposes.
            </p>
          </ModalBody>
        </Modal>
      )}
    </>
  );
}

/** ISO timestamp -> "31 July 2026" for customer-facing copy. */
function formatLongDate(value: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });
}
